import { useLanguage } from '@/context/LanguageContext';
import Image from 'next/image';
import React, { useState } from 'react';

type Column = {
    textMn: string;
    textEn: string;
};

type Cell = {
    banner?: string
    icon?: string;
    textEn: string;
    textMn: string;
    textColor?: string;
    bannerColor?: string
};

type Row = {
    cells?: Cell[];
};

type TableData = {
    columns: Column[];
    rows: Row[];
};

type DynamicTableProps = {
    data: TableData;
};

const DynamicTable: React.FC<DynamicTableProps> = ({ data }) => {
    const { locale } = useLanguage();
    const [searchTerm, setSearchTerm] = useState('');

    // Filter the rows based on the search term
    const filteredRows = data.rows.filter(row => {
        return row.cells?.some(cell => {
            const text = locale === 'en' ? cell.textEn : cell.textMn;
            return text.toLowerCase().includes(searchTerm.toLowerCase());
        });
    });

    return (
        <div>
            <div className="mb-4 flex justify-center items-center mt-4">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={`Search...`}
                    className="px-4 py-2 border border-secondaryMidStroke rounded w-1/2"
                />
            </div>
            <div className='xl:border xl:border-secondaryMidStroke flex flex-col rounded-xl overflow-auto w-full'>
                <table className="w-full text-sm text-left rtl:text-right">
                    <thead className="text-xs uppercase bg-surfaceLow">
                        <tr>
                            {data?.columns?.map((column, index) => (
                                <th key={index} className="px-6 py-3 text-center text-textHigh">
                                    {locale === 'en' ? column.textEn : column.textMn}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRows.length > 0 ? (
                            filteredRows.map((row, rowIndex) => (
                                <tr key={rowIndex} className="border-b border-secondaryMidStroke">
                                    {row.cells ? (
                                        row.cells.map((cell, cellIndex) => (
                                            <td key={cellIndex} className="py-4" style={{ color: cell.textColor }}>
                                                <div className="flex items-center justify-center w-[50px] md:w-full md:p-5 relative">
                                                    <p className='rounded-br-lg text-white font-semibold p-1 mx-2 absolute w-[100px] -top-[67px] -left-[9px] sm:-top-[67px] xl:-top-[16px] xl:-left-[9px]' style={{backgroundColor: cell.bannerColor}}>{cell.banner}</p>
                                                    {cell.icon && (
                                                        <Image
                                                            src={cell.icon}
                                                            alt="icon"
                                                            width={100}
                                                            height={100}
                                                            className="mr-2 w-[30px] h-[30px] rounded-xl"
                                                        />
                                                    )}
                                                    <p style={{ color: cell.textColor, textAlign: 'center'}} className={`${cell.textColor === undefined ? 'text-textHigh text-center' : ''}`}>
                                                        {locale === 'en' ? cell.textEn : cell.textMn}
                                                    </p>
                                                </div>
                                            </td>
                                        ))
                                    ) : (
                                        <td colSpan={data?.columns?.length} className="px-6 py-4 text-center text-textHigh">
                                            No data available
                                        </td>
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={data?.columns?.length} className="px-6 py-4 text-center text-textHigh">
                                    No matching results
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DynamicTable;
